// src/components/AiAgent.tsx
import React, { useEffect, useRef, useState } from "react";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { invokeBedrockAgent } from "core";

type ChatMsg = {
    id: string;
    role: "user" | "assistant" | "system";
    content: string;
};

export default function AiAgent() {
    const [messages, setMessages] = useState<ChatMsg[]>([
        {
            id: "welcome",
            role: "assistant",
            content: "Hey! I’m your AI Agent. Ask me anything.",
        },
    ]);
    const [input, setInput] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const listRef = useRef<HTMLDivElement | null>(null);

    // Auto-scroll to bottom
    useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
        }
    }, [messages, isTyping]);

    async function handleSend() {
        const text = input.trim();
        if (!text || isTyping) return;

        const userMsg: ChatMsg = { id: crypto.randomUUID(), role: "user", content: text };
        setMessages((m) => [...m, userMsg]);
        setInput("");
        setIsTyping(true);

        try {
            const res = await invokeBedrockAgent({ inputText: text });
            const data = res?.data ?? res;

            const assistantText =
                typeof data?.response === "string" ? data.response : JSON.stringify(data, null, 2);

            const botMsg: ChatMsg = {
                id: crypto.randomUUID(),
                role: "assistant",
                content: assistantText,
            };
            setMessages((m) => [...m, botMsg]);
        } catch (e: any) {
            const errMsg: ChatMsg = {
                id: crypto.randomUUID(),
                role: "system",
                content:
                    e?.response?.data?.message ||
                    e?.message ||
                    "Something went wrong. Please try again.",
            };
            setMessages((m) => [...m, errMsg]);
        } finally {
            setIsTyping(false);
        }
    }

    function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    }

    return (
        <div className="h-[100dvh] flex flex-col">
            {/* Header */}
            <div className="sticky top-0 z-10 border-b /90 dark:bg-dark-800/80 backdrop-blur modal-overlay/0 ms-5">
                <div className="container-responsive py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-xl bg-blue-600 grid place-items-center shadow-sm">
                            <span className="text-white font-bold">AI</span>
                        </div>
                        <div className="leading-tight">
                            <div className="font-semibold text-gray-800 dark:text-gray-200">AI Agent</div>
                            <div className="text-xs text-gray-700 dark:text-gray-300">
                                Chat with your assistant about campaigns & more
                            </div>
                        </div>
                    </div>
                    <div className="hidden sm:flex items-center gap-2">
                        <span className="status-dot success" />
                        <span className="text-xs text-gray-700 dark:text-gray-300">Online</span>
                    </div>
                </div>
            </div>

            {/* Chat area */}
            <div className="flex-1 overflow-y-auto">
                <div className="container-responsive h-full py-6">
                    <div className="card p-4 !pr-2 sm:p-6 min-h-[70vh] bg-grid-pattern">
                        {/* 👇 added no-scrollbar */}
                        <div
                            ref={listRef}
                            className="max-h-[65vh] overflow-y-auto no-scrollbar pr-1 space-y-4 fade-in"
                        >
                            {messages.map((m) => (
                                <MessageBubble key={m.id} role={m.role} content={m.content} />
                            ))}
                            {isTyping && <TypingRow />}
                        </div>
                    </div>
                </div>
            </div>

            {/* Composer */}
            <div className="sticky bottom-0 z-10 border-t /90 dark:bg-dark-800/80 backdrop-blur">
                <div className="container-responsive py-3">
                    <div className="card bg-surface p-2 sm:p-3">
                        <div className="flex gap-2 mobile-stack">
                            <Input
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={onKeyDown}
                                disabled={isTyping}
                                placeholder="Type your message…"
                                className="input-field mobile-full bg-none border-none focus:border-none focus:ring-0 focus:outline-none shadow-none"
                            />
                            <Button
                                className="btn-primary mobile-full"
                                onClick={handleSend}
                                disabled={isTyping || !input.trim()}
                                loading={isTyping}
                            >
                                {isTyping ? (
                                    "Thinking..."
                                ) : (
                                    "Send"
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ---------- UI bits ---------- */

function MessageBubble({
    role,
    content,
}: {
    role: "user" | "assistant" | "system";
    content: string;
}) {
    const isUser = role === "user";
    const isSystem = role === "system";
    const looksLikeJson = /^\s*[{[]/.test(content);

    return (
        <div className={`flex ${isUser ? "justify-end" : "justify-start"} slide-in-up`}>
            <div
                className={[
                    "max-w-[90%] sm:max-w-[75%] rounded-2xl px-4 py-3 shadow-sm smooth-transition",
                    // Use white surfaces (your theme will adapt in dark via `.dark .`)
                    isSystem
                        ? "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800/50"
                        : isUser
                            ? "bg-blue-600 text-white"
                            : " text-content border border-line",
                    "hover-lift",
                ].join(" ")}
            >
                {looksLikeJson ? (
                    <pre className="text-xs whitespace-pre-wrap break-words">{content}</pre>
                ) : (
                    <p className="whitespace-pre-wrap text-[0.95rem] leading-relaxed">{content}</p>
                )}
            </div>
        </div>
    );
}

function TypingRow() {
    return (
        <div className="flex justify-start">
            <div className="bg-surface border border-line rounded-2xl px-4 py-3 shadow-sm">
                <div className="flex items-center gap-3">
                    <TypingDots />
                </div>
            </div>
        </div>
    );
}

function TypingDots() {
    return (
      <>
        <style>
          {`
            @keyframes typingDot {
              0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
              40% { transform: scale(1); opacity: 1; }
            }
            .dot {
              width: 6px;
              height: 6px;
              border-radius: 50%;
              background-color: rgb(156 163 175); /* gray-400 */
              display: inline-block;
              animation: typingDot 1s infinite ease-in-out;
            }
          `}
        </style>
        <span className="inline-flex items-center gap-1">
          <span className="dot" style={{ animationDelay: "0s" }} />
          <span className="dot" style={{ animationDelay: "0.2s" }} />
          <span className="dot" style={{ animationDelay: "0.4s" }} />
        </span>
      </>
    );
  }
  